import { GRID_PHASES, SimChargeBox } from '../SmartChargingSimulator/scenario';

/**
 * Potência fixa por conector do modo estático (MV2-2005).
 *
 * Mesmas regras do smartcharging ao salvar: todo conector do grupo com valor, entre 6A e o máximo
 * do conector, e a soma (todos carregando juntos) dentro do limite menos a reserva de 6A. Soma total
 * no Geral, fase a fase no Por Fases.
 */

export const MODULATION_DYNAMIC = 'DYNAMIC';
export const MODULATION_STATIC = 'STATIC';

// No modo fixo o algoritmo só decide como a soma é conferida (os valores do backend continuam os mesmos)
export const fixedPowerLimitOptions = [
  { value: 'EQUALIZE', label: 'Soma total', id: 1 },
  { value: 'PHASE', label: 'Cada fase', id: 2 },
];

export const smartChargingModulationOptions = [
  { value: MODULATION_DYNAMIC, label: 'Dinâmico', id: 1 },
  { value: MODULATION_STATIC, label: 'Potência fixa', id: 2 },
];

export const MIN_CURRENT_A = 6;
export const SECURITY_A = 6;

export type ConnectorPower = {
  connector_id: string;
  power_value: number | null;
};

export type FixedPowerRow = {
  connector_id: string;
  station: string;
  seq: number;
  /** Fase/rotação da estação; sem fase conta nas três */
  phase: string | null;
  current_max_a: number | null;
  /** Potência máxima do conector cadastrada na estação (kW) */
  power_max_kw: number | null;
};

export type FixedPowerSum = {
  label: string;
  /** null = soma total (Geral) */
  phase: string | null;
  sum: number;
  limit: number;
};

export const fixedPowerRows = (chargeBoxes: SimChargeBox[]): FixedPowerRow[] =>
  chargeBoxes.flatMap((chargeBox) =>
    chargeBox.connectors
      .filter((connector) => !!connector.connector_id)
      .map((connector) => ({
        connector_id: connector.connector_id as string,
        station: chargeBox.code,
        seq: connector.seq,
        phase: chargeBox.phase,
        current_max_a: connector.current_max_a,
        power_max_kw: connector.power_max_kw ?? null,
      })),
  );

export const powerOf = (
  powers: ConnectorPower[] | null | undefined,
  connectorId: string,
) => {
  const power = (powers || []).find((p) => p.connector_id === connectorId);
  return power && power.power_value !== null && power.power_value !== undefined
    ? Number(power.power_value)
    : null;
};

/**
 * Modo e potências para criar a configuração. No fixo vão só os conectores das estações
 * selecionadas: uma estação tirada da seleção depois de preenchida não pode ir junto.
 */
export const modulationPayload = (
  smartCharging:
    | { modulation?: string; connector_powers?: ConnectorPower[] | null }
    | null
    | undefined,
  rows: FixedPowerRow[],
): { modulation: string; connector_powers?: ConnectorPower[] } => {
  if (smartCharging?.modulation !== MODULATION_STATIC) {
    return { modulation: MODULATION_DYNAMIC };
  }
  const groupConnectors = new Set(rows.map((row) => row.connector_id));
  return {
    modulation: MODULATION_STATIC,
    connector_powers: (smartCharging.connector_powers || []).filter((power) =>
      groupConnectors.has(power.connector_id),
    ),
  };
};

/** Troca (ou inclui) a potência de um conector, sem mexer nos demais */
export const setPower = (
  powers: ConnectorPower[] | null | undefined,
  connectorId: string,
  value: number | null,
): ConnectorPower[] => {
  const others = (powers || []).filter((p) => p.connector_id !== connectorId);
  return [...others, { connector_id: connectorId, power_value: value }];
};

const usesPhase = (row: FixedPowerRow, phase: string) =>
  !row.phase || row.phase.includes(phase);

const format = (value: number) =>
  Number.isInteger(value) ? `${value}` : value.toFixed(1);

export const fixedPowerSums = (
  algorithm: string | null | undefined,
  nominalA: number,
  rows: FixedPowerRow[],
  powers: ConnectorPower[] | null | undefined,
): FixedPowerSum[] => {
  const limit = nominalA - SECURITY_A;
  const sumOf = (filter: (row: FixedPowerRow) => boolean) =>
    rows
      .filter(filter)
      .reduce(
        (total, row) => total + (powerOf(powers, row.connector_id) || 0),
        0,
      );

  if (algorithm === 'PHASE') {
    return GRID_PHASES.map((phase) => ({
      label: `Fase ${phase}`,
      phase,
      sum: sumOf((row) => usesPhase(row, phase)),
      limit,
    }));
  }
  return [{ label: 'Total', phase: null, sum: sumOf(() => true), limit }];
};

/**
 * Maior potência que o conector pode receber sem estourar o limite: o que sobra do limite (menos a
 * reserva) descontando os outros conectores, no total ou em cada fase que ele usa, e o máximo do
 * conector. Nunca abaixo do mínimo, para o campo continuar editável (o erro da soma aparece).
 */
export const fixedPowerMax = (
  algorithm: string | null | undefined,
  nominalA: number,
  rows: FixedPowerRow[],
  powers: ConnectorPower[] | null | undefined,
  row: FixedPowerRow,
): number | undefined => {
  const caps: number[] = [];
  if (row.current_max_a) {
    caps.push(row.current_max_a);
  }
  if (nominalA) {
    const others = rows.filter((r) => r.connector_id !== row.connector_id);
    const room = (filter: (r: FixedPowerRow) => boolean) =>
      nominalA -
      SECURITY_A -
      others
        .filter(filter)
        .reduce(
          (total, r) => total + (powerOf(powers, r.connector_id) || 0),
          0,
        );
    if (algorithm === 'PHASE') {
      GRID_PHASES.filter((phase) => usesPhase(row, phase)).forEach((phase) =>
        caps.push(room((r) => usesPhase(r, phase))),
      );
    } else {
      caps.push(room(() => true));
    }
  }
  if (!caps.length) {
    return undefined;
  }
  return Math.max(MIN_CURRENT_A, Math.floor(Math.min(...caps)));
};

export const fixedPowerErrors = (
  algorithm: string | null | undefined,
  nominalA: number | string | null | undefined,
  rows: FixedPowerRow[],
  powers: ConnectorPower[] | null | undefined,
): string[] => {
  const errors: string[] = [];

  rows.forEach((row) => {
    const name = `${row.station} conector ${row.seq}`;
    const power = powerOf(powers, row.connector_id);
    if (power === null) {
      errors.push(`${name} sem potência fixa`);
    } else if (power < MIN_CURRENT_A) {
      errors.push(
        `${name}: ${format(power)}A abaixo do mínimo de ${MIN_CURRENT_A}A`,
      );
    } else if (row.current_max_a && power > row.current_max_a) {
      errors.push(
        `${name}: ${format(power)}A acima do máximo do conector (${format(
          row.current_max_a,
        )}A)`,
      );
    }
  });

  const nominal = Number(nominalA);
  if (!nominalA || Number.isNaN(nominal)) {
    return errors;
  }
  fixedPowerSums(algorithm, nominal, rows, powers)
    .filter(({ sum, limit }) => sum > limit)
    .forEach(({ phase, sum, limit }) =>
      errors.push(
        phase
          ? `soma da fase ${phase} (${format(
              sum,
            )}A) passa do limite do grupo menos a reserva (${format(limit)}A)`
          : `soma das potências (${format(
              sum,
            )}A) passa do limite do grupo menos a reserva (${format(limit)}A)`,
      ),
    );
  return errors;
};

const clampToConnector = (row: FixedPowerRow, value: number) =>
  Math.max(
    MIN_CURRENT_A,
    row.current_max_a ? Math.min(value, row.current_max_a) : value,
  );

/**
 * Divide o limite (menos a reserva) igualmente entre os conectores, em ampères inteiros: no total,
 * ou em cada fase pela fase mais cheia que o conector usa. Respeita o máximo de cada conector.
 */
export const evenSplit = (
  algorithm: string | null | undefined,
  nominalA: number,
  rows: FixedPowerRow[],
): ConnectorPower[] => {
  const limit = nominalA - SECURITY_A;
  const share = (row: FixedPowerRow) => {
    if (algorithm !== 'PHASE') {
      return limit / rows.length;
    }
    return Math.min(
      ...GRID_PHASES.filter((phase) => usesPhase(row, phase)).map(
        (phase) => limit / rows.filter((r) => usesPhase(r, phase)).length,
      ),
    );
  };
  return rows.map((row) => ({
    connector_id: row.connector_id,
    power_value: clampToConnector(row, Math.floor(share(row))),
  }));
};

/** Mesmo valor nos conectores escolhidos (limitado ao máximo de cada um); os demais não mudam */
export const applyToAll = (
  powers: ConnectorPower[] | null | undefined,
  rows: FixedPowerRow[],
  value: number,
): ConnectorPower[] =>
  rows.reduce(
    (current, row) =>
      setPower(current, row.connector_id, clampToConnector(row, value)),
    powers || [],
  );

/** "7,04 kW · 32 A": potência cadastrada na estação e, quando o CS conhece, a corrente máxima */
export const chargerMaxText = (row: FixedPowerRow) => {
  const parts: string[] = [];
  if (row.power_max_kw) {
    parts.push(
      `${row.power_max_kw.toLocaleString('pt-BR', {
        maximumFractionDigits: 2,
      })} kW`,
    );
  }
  if (row.current_max_a) {
    parts.push(
      `${row.current_max_a.toLocaleString('pt-BR', {
        maximumFractionDigits: 1,
      })} A`,
    );
  }
  return parts.length ? parts.join(' · ') : '-';
};
